import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/buildern-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Buildern Alternative for UK Builders",
  "description": "Looking for a Buildern alternative for UK residential builders? The Proper Tool offers AI-powered quoting, a 31-category UK cost library, and a 14-day free trial from £29/mo.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "Buildern Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best Buildern alternative for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK residential builders who primarily need fast, accurate construction quoting rather than full project management, The Proper Tool is the strongest Buildern alternative. Buildern is a cloud-based construction project management and estimating platform with strong Gantt chart and scheduling features, but it is not calibrated for UK residential construction rates and has no AI scope generation. The Proper Tool starts at £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Is Buildern designed for UK residential builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Buildern is a US-origin cloud construction management platform. Its estimating module is not calibrated for UK residential construction labour and material rates. UK builders typically need to configure their own rate schedules, which adds significant setup time and reduces the built-in value of the platform.",
      },
    },
    {
      "@type": "Question",
      "name": "Does Buildern have AI quote generation from drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Buildern does not generate quotes from uploaded drawings or project descriptions using AI. Its estimating requires manual item selection and quantity entry. The Proper Tool reads uploaded structural drawing PDFs using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete quote.",
      },
    },
    {
      "@type": "Question",
      "name": "How does Buildern compare to The Proper Tool for small builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Buildern is primarily suited to larger building firms and project managers who need integrated scheduling, Gantt views, and budget tracking across multi-month projects. For small UK builders and sole traders who primarily need fast quoting, Buildern is more platform than the job requires. The Proper Tool is purpose-built for construction quoting, starts at £29/month, and requires no configuration before the first quote.",
      },
    },
    {
      "@type": "Question",
      "name": "How does Buildern pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Buildern pricing varies by plan and typically increases with team size and feature access. The Proper Tool starts at £29 per month with a 14-day free trial and no credit card required, making it significantly more accessible for small UK builders and sole traders.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  buildern: string;
  buildernState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "UK residential construction pricing", buildern: "Not calibrated for UK", buildernState: "no", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "AI scope generation from description", buildern: "No: manual entry only", buildernState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", buildern: "No", buildernState: "no", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "Scope completeness validation", buildern: "No", buildernState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Labour / materials / plant split", buildern: "Partial", buildernState: "partial", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Project scheduling and Gantt charts", buildern: "Yes (strong)", buildernState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Budget tracking across projects", buildern: "Yes", buildernState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Suited to small builders and sole traders", buildern: "Partial: project-manager focus", buildernState: "partial", tpt: "Yes: built for this", tptState: "yes" },
  { feature: "Branded PDF quote output", buildern: "Yes", buildernState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Free trial available", buildern: "Limited trial", buildernState: "partial", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Starting price", buildern: "Varies by plan and team size", buildernState: "partial", tpt: "£29/month (per account)", tptState: "yes" },
  { feature: "Time to first quote", buildern: "Setup and configuration required", buildernState: "partial", tpt: "Under 10 minutes", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "US-origin platform with no UK residential pricing",
    desc: "Buildern is a US-origin platform. Its estimating is not calibrated for UK residential construction labour and material rates. UK builders need to manually configure their own rate schedules before the estimating module produces realistic UK-market output.",
  },
  {
    title: "No AI scope generation from descriptions or drawings",
    desc: "Buildern requires manual item selection and quantity entry for every estimate. There is no AI that reads a project description or uploaded drawing and generates a complete scope. For builders quoting from architects' drawings or written briefs, this adds significant manual work.",
  },
  {
    title: "Built for project management, not quoting speed",
    desc: "Buildern's strengths are Gantt chart scheduling, budget tracking across multi-month projects, and project management for larger firms. For small builders and sole traders who primarily need fast, accurate quoting, this depth of project management is more platform than they require.",
  },
  {
    title: "Overkill for small and sole-trader builders",
    desc: "Buildern is built around the needs of project managers and larger building firms. For sole traders and small teams doing a handful of extension or alteration jobs per month, the platform complexity and cost is difficult to justify against the quoting value it delivers.",
  },
];

const STAY_ON_BUILDERN = [
  "You are a project manager or larger building firm that needs integrated scheduling, Gantt views, and budget tracking across multi-month projects.",
  "Your primary need is managing the full project lifecycle beyond just quoting.",
  "You have already invested the time to configure Buildern for your workflows and rate schedules.",
  "You need team collaboration and shared project dashboards across a larger organisation.",
];

const SWITCH_REASONS = [
  "You are a small builder or sole trader and your primary need is fast, accurate construction quoting, not project management.",
  "You want UK-calibrated pricing built in, not a US-origin platform you have to manually configure.",
  "You want to generate a complete quote from drawings or a description in under 10 minutes.",
  "You want a tool specifically built for extensions, loft conversions, and residential alterations.",
  "You want a 14-day free trial with no credit card, so you can test the tool on real jobs before committing.",
  "You want transparent monthly pricing with no per-seat fees or usage-based billing surprises.",
];

const FAQS = [
  {
    q: "What is the best Buildern alternative for UK builders?",
    a: "For UK residential builders who primarily need fast, accurate construction quoting, The Proper Tool is the strongest Buildern alternative. Buildern is a cloud-based construction project management platform, but it is not calibrated for UK residential construction rates and has no AI scope generation. The Proper Tool starts at £29/month with a 14-day free trial.",
  },
  {
    q: "Is Buildern designed for UK residential builders?",
    a: "Buildern is a US-origin platform and its estimating module is not calibrated for UK residential construction labour and material rates. UK builders typically need to configure their own rate schedules, which adds significant setup time.",
  },
  {
    q: "Does Buildern have AI quote generation from drawings?",
    a: "No. Buildern does not generate quotes from uploaded drawings or project descriptions using AI. The Proper Tool reads uploaded structural drawing PDFs using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete quote.",
  },
  {
    q: "How does Buildern compare to The Proper Tool for small builders?",
    a: "Buildern is primarily suited to larger building firms who need project scheduling, Gantt views, and budget tracking. For small UK builders and sole traders, The Proper Tool is purpose-built for construction quoting, starts at £29/month, and requires no configuration before the first quote.",
  },
  {
    q: "How does Buildern pricing compare to The Proper Tool?",
    a: "Buildern pricing varies by plan and typically increases with team size. The Proper Tool starts at £29 per month with a 14-day free trial and no credit card required, making it significantly more accessible for small UK builders and sole traders.",
  },
];

export default function BuildernAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Buildern Alternative for UK Builders: Fast Construction Quoting Without the Project Management Overhead | The Proper Tool"
        description="Looking for a Buildern alternative for UK residential builders? The Proper Tool offers AI-powered quoting, a 31-category UK cost library, and a 14-day free trial from £29/mo."
        canonical="/buildern-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Buildern Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Buildern Alternative for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Buildern is a cloud-based project management and estimating platform. If you are a UK residential builder who needs fast, AI-powered quoting rather than Gantt charts and project scheduling, here is an honest comparison.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why UK builders look for a Buildern alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Buildern is a capable construction management platform with strong project scheduling features. But UK residential builders consistently hit the same limitations when they try to use it for quoting:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs Buildern: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Buildern</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.buildern} state={row.buildernState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
              Competitor pricing and feature information is based on publicly available data at time of writing. Prices and features may have changed. Verify directly with Buildern before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Buildern pricing varies by plan and scales with team size and feature access. For small UK builders who primarily need quoting, paying for a full project management platform is a significant overhead. The Proper Tool offers transparent per-account pricing from £29/month.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">Buildern</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Pricing varies by plan and team size</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />US-origin pricing, not calibrated for UK construction</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No AI scope generation or drawing interpretation</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Configuration required before first useful quote</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month (per account)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial, no credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />UK cost library built in, first quote in under 10 minutes</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No per-seat fees: one price for the whole account</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on Buildern</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Buildern is genuinely strong for a specific type of construction business. If the following describes your situation, it may be the right choice:
            </p>
            <div className="space-y-3">
              {STAY_ON_BUILDERN.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons UK builders switch from Buildern to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically from a project management platform to a tool built specifically for UK construction quoting.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              UK-built quoting software for residential builders. Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with Buildern before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/buildxact-alternative", label: "Buildxact Alternative UK" },
                { href: "/simpro-alternative", label: "simPRO Alternative for Small Builders" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
